import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useSearchParams } from 'react-router'
import type { VehiclesProviderVehiclesFragment } from './VehiclesProvider.hook'

export type VehiclesContextValue<T extends VehiclesProviderVehiclesFragment> = {
  vehicles: T[]
  loadingVehicles: boolean
  selectedVehicle: T | undefined
  wasVehicleSelectedBySearch: boolean
  selectVehicle: (id: string, deselectIfSelected?: boolean) => void
  searchVehicle: (id?: string) => void
  clearVehicleSelection: () => void
}

type CreateVehiclesProviderProps<T extends VehiclesProviderVehiclesFragment> = {
  useVehiclesQuery: () => {
    vehicles: T[]
    loading: boolean
  }
}

export const createVehiclesProvider = <
  T extends VehiclesProviderVehiclesFragment,
>({
  useVehiclesQuery,
}: CreateVehiclesProviderProps<T>) => {
  const VehiclesContext = createContext<VehiclesContextValue<T> | null>(null)

  const Provider = ({ children }: { children: React.ReactNode }) => {
    const { vehicles, loading: loadingVehicles } = useVehiclesQuery()

    const [selectedVehicleId, setSelectedVehicleId] = useState<string>()
    const [wasVehicleSelectedBySearch, setIsVehicleSelectedViaSearch] =
      useState(false)

    const [searchParams, setSearchParams] = useSearchParams()
    const urlParamVehicleId = searchParams.get('vehicleId')

    const selectedVehicleIdRef = useRef(selectedVehicleId)
    selectedVehicleIdRef.current = selectedVehicleId

    const clearVehicleSelection = useCallback(() => {
      setSearchParams({})
      setSelectedVehicleId(undefined)
      setIsVehicleSelectedViaSearch(false)
    }, [setSearchParams])

    const selectVehicle = useCallback(
      (id: string, deselectIfSelected = false) => {
        console.info('[AdVehicleStream] selectVehicle called', {
          vehicleId: id,
          deselectIfSelected,
          currentSelectedVehicleId: selectedVehicleIdRef.current,
        })
        if (selectedVehicleIdRef.current === id && deselectIfSelected) {
          clearVehicleSelection()
        } else {
          setSearchParams({ vehicleId: id })
          setIsVehicleSelectedViaSearch(false)
        }
      },
      [setSearchParams, clearVehicleSelection],
    )

    const searchVehicle = useCallback(
      (id?: string) => {
        if (id === undefined) {
          setIsVehicleSelectedViaSearch(false)
          return
        }

        if (selectedVehicleIdRef.current !== id) {
          setSearchParams({ vehicleId: id })
          setIsVehicleSelectedViaSearch(true)
        }
      },
      [setSearchParams],
    )

    const selectedVehicle = useMemo(
      () => vehicles.find(v => v.id === selectedVehicleId) || undefined,
      [selectedVehicleId, vehicles],
    )

    // Sync with URL parameter
    useEffect(() => {
      if (!loadingVehicles) {
        if (urlParamVehicleId) {
          if (vehicles.some(v => v.id === urlParamVehicleId)) {
            setSelectedVehicleId(urlParamVehicleId)
          } else {
            setSearchParams({})
          }
        } else {
          setSelectedVehicleId(undefined)
          setIsVehicleSelectedViaSearch(false)
        }
      }
    }, [urlParamVehicleId, vehicles, loadingVehicles, setSearchParams])

    const contextValue = useMemo(
      () => ({
        vehicles,
        loadingVehicles,
        selectedVehicle,
        wasVehicleSelectedBySearch,
        selectVehicle,
        searchVehicle,
        clearVehicleSelection,
      }),
      [
        vehicles,
        loadingVehicles,
        selectedVehicle,
        wasVehicleSelectedBySearch,
        selectVehicle,
        searchVehicle,
        clearVehicleSelection,
      ],
    )

    return (
      <VehiclesContext.Provider value={contextValue}>
        {children}
      </VehiclesContext.Provider>
    )
  }

  const useVehicles = () => {
    const vehiclesContextValue = useContext(VehiclesContext)

    if (vehiclesContextValue === null) {
      throw new Error('useVehicles must be used within VehiclesProvider')
    }

    return vehiclesContextValue
  }

  return { Provider, useVehicles }
}
